import { useCallback, useEffect, useState } from "react";
import { api, apiError, waktu } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { ErpLayout, Card, Table } from "@/erp/shell";
import { Modal } from "@/erp/AdminPages";
import { MediaUploadField } from "@/erp/MediaUploadField";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const inp = "w-full rounded-lg border border-[#E5DEC9] px-3 py-2 text-sm outline-none focus:border-[#C5A028]";
const label = "block text-xs font-bold uppercase tracking-wider text-[#4A4844] mb-1";

export function CmsAdmin() {
  const { can } = useAuth();
  const [tab, setTab] = useState("artikel");
  const [posts, setPosts] = useState([]);
  const [pages, setPages] = useState([]);
  const [shop, setShop] = useState([]);
  const [form, setForm] = useState(null);
  const [shopForm, setShopForm] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/posts").then((r) => setPosts(r.data)).catch(() => {});
    api.get("/admin/pages").then((r) => setPages(r.data)).catch(() => {});
    api.get("/admin/shop-products").then((r) => setShop(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const savePost = async (e) => {
    e.preventDefault();
    try {
      if (form._id) await api.put(`/admin/posts/${form._id}`, form);
      else await api.post("/admin/posts", form);
      toast.success("Artikel disimpan"); setForm(null); load();
    } catch (e2) { toast.error(apiError(e2)); }
  };
  const delPost = async (id) => {
    try { await api.delete(`/admin/posts/${id}`); toast.success("Artikel dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveShop = async (e) => {
    e.preventDefault();
    const body = {
      ...shopForm,
      price: Number(shopForm.price),
      stock: Number(shopForm.stock),
      commission_pool_per_item: Number(shopForm.commission_pool_per_item || 0),
      commission_enabled: !!shopForm.commission_enabled,
    };
    delete body._id;
    try {
      if (shopForm._id) await api.put(`/admin/shop-products/${shopForm._id}`, body);
      else await api.post("/admin/shop-products", body);
      toast.success("Produk toko disimpan"); setShopForm(null); load();
    } catch (e2) { toast.error(apiError(e2)); }
  };
  const delShop = async (id) => {
    try { await api.delete(`/admin/shop-products/${id}`); toast.success("Produk dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <ErpLayout title="Website & CMS" breadcrumb={["Website & CMS"]}
      actions={can("cms.write") && (tab === "artikel" ? (
        <button onClick={() => setForm({ title: "", excerpt: "", content: "", cover_image: "", category: "Panduan", status: "draft", seo_title: "", seo_description: "" })}
          data-testid="post-new-btn" className="btn-gold rounded-xl px-4 py-2.5 text-sm font-bold"><Plus size={14} className="mr-1 inline" /> Artikel Baru</button>
      ) : tab === "toko" ? (
        <button onClick={() => setShopForm({ name: "", category_slug: "oleh-oleh", description: "", price: 0, stock: 0, image: "", status: "published" })}
          data-testid="shopitem-new-btn" className="btn-gold rounded-xl px-4 py-2.5 text-sm font-bold"><Plus size={14} className="mr-1 inline" /> Produk Toko</button>
      ) : null)}>
      <div className="mb-5 flex gap-2">
        {[["artikel", "Artikel"], ["halaman", "Halaman"], ["toko", "Produk Toko"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`cms-tab-${k}`}
            className={`rounded-full px-4 py-2 text-sm font-semibold ${tab === k ? "bg-[#141414] text-white" : "border border-[#E5DEC9] bg-white"}`}>{l}</button>))}
      </div>

      {tab === "artikel" && (
        <Table testid="post-table" columns={["Judul", "Kategori", "Status", "Diperbarui", "Aksi"]}
          rows={posts.map((p) => (
            <tr key={p.id} className="border-t border-[#F4EFE6]" data-testid={`post-row-${p.id}`}>
              <td className="p-4"><div className="font-semibold">{p.title}</div><div className="text-xs text-[#8A867C]">/{p.slug}</div></td>
              <td className="p-4">{p.category}</td>
              <td className="p-4"><span className={`rounded-full px-2.5 py-1 text-xs font-bold ${p.status === "published" ? "bg-[#F5F1E8] text-[#15803D]" : "bg-[#F4EFE6] text-[#B45309]"}`}>{p.status}</span></td>
              <td className="p-4">{waktu(p.updated_at)}</td>
              <td className="p-4">{can("cms.write") && (
                <div className="flex gap-2">
                  <button onClick={() => setForm({ ...p, _id: p.id })} data-testid={`post-edit-${p.id}`} className="rounded-lg border border-[#E5DEC9] px-3 py-1.5 text-xs font-semibold">Edit</button>
                  <button onClick={() => delPost(p.id)} data-testid={`post-delete-${p.id}`} className="rounded-lg border border-[#F0C4C4] p-2 text-[#B91C1C]"><Trash2 size={13} /></button>
                </div>)}</td>
            </tr>))} />)}

      {tab === "halaman" && (
        <Table testid="page-table" columns={["Judul", "Slug", "Status", "Diperbarui"]}
          rows={pages.map((p) => (
            <tr key={p.id} className="border-t border-[#F4EFE6]" data-testid={`page-row-${p.id}`}>
              <td className="p-4 font-semibold">{p.title}</td><td className="p-4">/{p.slug}</td>
              <td className="p-4">{p.status}</td><td className="p-4">{waktu(p.updated_at)}</td>
            </tr>))} />)}

      {tab === "toko" && (
        <Table testid="shopitem-table" columns={["Produk", "Kategori", "Harga", "Stok", "Pool Komisi", "Status", "Aksi"]}
          rows={shop.map((s) => (
            <tr key={s.id} className="border-t border-[#F4EFE6]" data-testid={`shopitem-row-${s.id}`}>
              <td className="p-4 font-semibold">{s.name}</td><td className="p-4">{s.category_slug}</td>
              <td className="p-4">Rp {Number(s.price).toLocaleString("id-ID")}</td><td className="p-4">{s.stock}</td>
              <td className="p-4">
                {s.commission_enabled
                  ? <span className="text-xs font-semibold text-[#8C6D1F]">Rp {Number(s.commission_pool_per_item || 0).toLocaleString("id-ID")}</span>
                  : <span className="text-xs text-[#8A867C]">—</span>}
              </td>
              <td className="p-4">{s.status}</td>
              <td className="p-4">{can("product.write") && (
                <div className="flex gap-2">
                  <button onClick={() => setShopForm({ ...s, _id: s.id })} data-testid={`shopitem-edit-${s.id}`} className="rounded-lg border border-[#E5DEC9] px-3 py-1.5 text-xs font-semibold">Edit</button>
                  <button onClick={() => delShop(s.id)} data-testid={`shopitem-delete-${s.id}`} className="rounded-lg border border-[#F0C4C4] p-2 text-[#B91C1C]"><Trash2 size={13} /></button>
                </div>)}</td>
            </tr>))} />)}

      {form && (
        <Modal title={form._id ? "Edit Artikel" : "Artikel Baru"} onClose={() => setForm(null)} onSubmit={savePost} testid="post-form">
          <div className="sm:col-span-2"><label className={label}>Judul</label><input required className={inp} data-testid="af-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
          <div><label className={label}>Kategori</label><input className={inp} data-testid="af-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
          <div><label className={label}>Status</label>
            <select className={inp} data-testid="af-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="draft">Draft</option><option value="published">Published</option><option value="archived">Archived</option>
            </select></div>
          <div className="sm:col-span-2">
            <MediaUploadField label="Gambar Cover" testId="af-cover" context="cms"
              value={form.cover_image} onChange={(url) => setForm({ ...form, cover_image: url })}
              help="Upload gambar ke Object Storage atau tempel URL eksternal." />
          </div>
          <div className="sm:col-span-2"><label className={label}>Ringkasan</label><input className={inp} data-testid="af-excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} /></div>
          <div className="sm:col-span-2"><label className={label}>Isi Artikel</label><textarea rows={6} className={inp} data-testid="af-content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} /></div>
        </Modal>)}

      {shopForm && (
        <Modal title={shopForm._id ? "Edit Produk Toko" : "Produk Toko Baru"} onClose={() => setShopForm(null)} onSubmit={saveShop} testid="shopitem-form">
          <div className="sm:col-span-2"><label className={label}>Nama Produk</label><input required className={inp} data-testid="sf-name" value={shopForm.name} onChange={(e) => setShopForm({ ...shopForm, name: e.target.value })} /></div>
          <div><label className={label}>Kategori</label>
            <select className={inp} data-testid="sf-category" value={shopForm.category_slug} onChange={(e) => setShopForm({ ...shopForm, category_slug: e.target.value })}>
              <option value="oleh-oleh">Oleh-oleh Haramain</option><option value="perlengkapan-ibadah">Perlengkapan Ibadah</option><option value="buku-quran">Al-Qur'an & Buku</option>
            </select></div>
          <div><label className={label}>Status</label>
            <select className={inp} data-testid="sf-status" value={shopForm.status} onChange={(e) => setShopForm({ ...shopForm, status: e.target.value })}>
              <option value="published">Published</option><option value="draft">Draft</option>
            </select></div>
          <div><label className={label}>Harga (IDR)</label><input type="number" className={inp} data-testid="sf-price" value={shopForm.price} onChange={(e) => setShopForm({ ...shopForm, price: e.target.value })} /></div>
          <div><label className={label}>Stok</label><input type="number" className={inp} data-testid="sf-stock" value={shopForm.stock} onChange={(e) => setShopForm({ ...shopForm, stock: e.target.value })} /></div>
          <div className="sm:col-span-2">
            <MediaUploadField label="Gambar Produk" testId="sf-image" context="shop"
              value={shopForm.image} onChange={(url) => setShopForm({ ...shopForm, image: url })}
              help="Gambar toko disimpan persisten di Object Storage." />
          </div>
          <div className="sm:col-span-2"><label className={label}>Deskripsi</label><textarea rows={3} className={inp} data-testid="sf-desc" value={shopForm.description} onChange={(e) => setShopForm({ ...shopForm, description: e.target.value })} /></div>
          {/* Komisi Mitra */}
          <div className="sm:col-span-2 rounded-xl bg-[#FDF6E3] p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-[#141414]">Pool Komisi Mitra</p>
                <p className="text-xs text-[#8A867C]">Dibagi 40/20/40 ke Cabang / CP / Umrah Consultant</p>
              </div>
              <label className="relative inline-flex cursor-pointer items-center gap-2">
                <input type="checkbox" className="sr-only" data-testid="sf-commission-enabled"
                  checked={!!shopForm.commission_enabled}
                  onChange={(e) => setShopForm({ ...shopForm, commission_enabled: e.target.checked, commission_pool_per_item: e.target.checked ? (shopForm.commission_pool_per_item || 0) : 0 })} />
                <div className={`h-5 w-9 rounded-full transition-colors ${shopForm.commission_enabled ? "bg-[#8C6D1F]" : "bg-gray-300"}`}>
                  <div className={`mt-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${shopForm.commission_enabled ? "translate-x-4 ml-0.5" : "translate-x-0.5"}`} />
                </div>
                <span className="text-xs font-medium">{shopForm.commission_enabled ? "Aktif" : "Nonaktif"}</span>
              </label>
            </div>
            {shopForm.commission_enabled && (
              <div className="mt-3">
                <label className={label}>Pool Komisi per Item (IDR)</label>
                <input type="number" min={0} className={inp} data-testid="sf-commission-pool"
                  value={shopForm.commission_pool_per_item || ""}
                  placeholder="Contoh: 50000"
                  onChange={(e) => setShopForm({ ...shopForm, commission_pool_per_item: e.target.value })} />
              </div>
            )}
          </div>
        </Modal>)}
    </ErpLayout>
  );
}

export function SettingsAdmin() {
  const { can, user } = useAuth();
  const isSuperAdmin = user?.roles?.includes("super_admin");
  const [settings, setSettings] = useState([]);
  const [banks, setBanks] = useState([]);
  const [types, setTypes] = useState([]);
  const [audit, setAudit] = useState({ items: [], total: 0 });
  const [aq, setAq] = useState("");
  const [page, setPage] = useState(1);
  const [bank, setBank] = useState(null);
  const [dt, setDt] = useState("");

  const load = useCallback(() => {
    api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/admin/bank-accounts").then((r) => setBanks(r.data)).catch(() => {});
    api.get("/admin/document-types").then((r) => setTypes(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!can("audit.read")) return;
    api.get("/admin/audit-logs", { params: { q: aq || undefined, page, page_size: 10 } })
      .then((r) => setAudit(r.data)).catch(() => {});
  }, [aq, page, can]);

  const saveSetting = async (s, value) => {
    try { await api.put("/admin/settings", { key: s.key, value, is_public: s.is_public, group: s.group }); toast.success("Pengaturan disimpan"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveBank = async (e) => {
    e.preventDefault();
    try {
      if (bank._id) {
        await api.put(`/admin/bank-accounts/${bank._id}`, bank);
        toast.success("Rekening diperbarui");
      } else {
        await api.post("/admin/bank-accounts", bank);
        toast.success("Rekening ditambahkan");
      }
      setBank(null); load();
    } catch (e2) { toast.error(apiError(e2)); }
  };
  const delBank = async (id) => {
    if (!window.confirm("Hapus rekening ini? Tindakan tidak dapat dibatalkan.")) return;
    try { await api.delete(`/admin/bank-accounts/${id}`); toast.success("Rekening dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const addType = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/document-types", { name: dt }); toast.success("Jenis dokumen ditambahkan"); setDt(""); load(); }
    catch (e2) { toast.error(apiError(e2)); }
  };

  return (
    <ErpLayout title="Pengaturan" breadcrumb={["Pengaturan"]}>
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <div className="eyebrow">System Settings</div>
          <div className="mt-4 space-y-3" data-testid="settings-list">
            {settings.map((s) => (
              <div key={s.key} className="grid gap-2 sm:grid-cols-[150px_1fr]">
                <div className="text-xs font-bold uppercase text-[#4A4844]">{s.key}</div>
                <input defaultValue={s.value} disabled={!can("settings.write")} data-testid={`setting-${s.key}`}
                  onBlur={(e) => e.target.value !== s.value && saveSetting(s, e.target.value)} className={inp} />
              </div>))}
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div className="eyebrow">Rekening Perusahaan</div>
            {can("settings.write") && (
              <button onClick={() => setBank({ bank_name: "", account_number: "", account_holder: "", branch: "" })}
                data-testid="bank-new-btn" className="btn-gold rounded-lg px-3 py-1.5 text-xs font-bold"><Plus size={12} className="mr-1 inline" /> Rekening</button>)}
          </div>
          <div className="mt-4 space-y-2" data-testid="bank-list">
            {banks.map((b) => (
              <div key={b.id} className="flex items-center justify-between gap-2 rounded-lg border border-[#E5DEC9] p-3 text-sm" data-testid={`bank-row-${b.id}`}>
                <div className="min-w-0">
                  <div className="font-bold text-[#141414]">{b.bank_name}</div>
                  <div className="text-xs text-[#4A4844]">{b.account_number} · a.n. {b.account_holder}</div>
                  {b.branch && <div className="text-xs text-[#8A867C]">Cabang: {b.branch}</div>}
                </div>
                {isSuperAdmin && (
                  <div className="flex shrink-0 items-center gap-1.5">
                    <button
                      onClick={() => setBank({ ...b, _id: b.id })}
                      className="rounded-lg border border-[#E5DEC9] p-2 hover:border-[#C5A028] hover:bg-[#F4EFE6] transition-colors"
                      title="Edit rekening"
                      data-testid={`bank-edit-${b.id}`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#C5A028" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </button>
                    <button
                      onClick={() => delBank(b.id)}
                      className="rounded-lg border border-[#F0C4C4] p-2 text-[#B91C1C] hover:bg-[#FBE9E9] transition-colors"
                      title="Hapus rekening"
                      data-testid={`bank-delete-${b.id}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                )}
              </div>))}
            {banks.length === 0 && <p className="text-sm text-[#8A867C]">Belum ada rekening.</p>}
          </div>
          <div className="mt-6 eyebrow">Jenis Dokumen</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {types.map((t) => <span key={t.id} className="rounded-full bg-[#F4EFE6] px-3 py-1 text-xs font-semibold">{t.name}</span>)}
          </div>
          {can("document.write") && (
            <form onSubmit={addType} className="mt-3 flex gap-2" data-testid="doctype-form">
              <input required value={dt} onChange={(e) => setDt(e.target.value)} placeholder="Nama jenis dokumen baru" data-testid="doctype-input" className={inp} />
              <button className="btn-gold rounded-lg px-4 text-sm font-bold" data-testid="doctype-submit">Tambah</button>
            </form>)}
        </Card>
      </div>

      {can("audit.read") && (
        <Card className="mt-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="eyebrow">Audit Log ({audit.total})</div>
            <input value={aq} onChange={(e) => { setAq(e.target.value); setPage(1); }} placeholder="Cari aksi/entitas/email..." data-testid="audit-search" className={`${inp} max-w-xs`} />
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead className="bg-[#F4EFE6] text-left text-xs uppercase text-[#4A4844]">
                <tr><th className="p-3">Waktu</th><th className="p-3">Pengguna</th><th className="p-3">Aksi</th><th className="p-3">Entitas</th></tr>
              </thead>
              <tbody>
                {audit.items.map((a) => (
                  <tr key={a.id} className="border-t border-[#F4EFE6]" data-testid={`audit-row-${a.id}`}>
                    <td className="p-3">{waktu(a.created_at)}</td><td className="p-3">{a.user_email}</td>
                    <td className="p-3 font-semibold">{a.action}</td><td className="p-3">{a.entity}</td>
                  </tr>))}
                {audit.items.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-[#8A867C]">Belum ada log.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="audit-prev" className="rounded-lg border border-[#E5DEC9] px-3 py-1.5 text-sm disabled:opacity-40">Sebelumnya</button>
            <span className="text-sm text-[#4A4844]">Halaman {page}</span>
            <button disabled={page * 10 >= audit.total} onClick={() => setPage(page + 1)} data-testid="audit-next" className="rounded-lg border border-[#E5DEC9] px-3 py-1.5 text-sm disabled:opacity-40">Berikutnya</button>
          </div>
        </Card>)}

      {bank && (
        <Modal title={bank._id ? "Edit Rekening" : "Rekening Baru"} onClose={() => setBank(null)} onSubmit={saveBank} testid="bank-form">
          <div><label className={label}>Nama Bank</label><input required className={inp} data-testid="bf-bank" value={bank.bank_name} onChange={(e) => setBank({ ...bank, bank_name: e.target.value })} /></div>
          <div><label className={label}>Nomor Rekening</label><input required className={inp} data-testid="bf-number" value={bank.account_number} onChange={(e) => setBank({ ...bank, account_number: e.target.value })} /></div>
          <div><label className={label}>Atas Nama</label><input required className={inp} data-testid="bf-holder" value={bank.account_holder} onChange={(e) => setBank({ ...bank, account_holder: e.target.value })} /></div>
          <div><label className={label}>Cabang Bank</label><input className={inp} data-testid="bf-branch" value={bank.branch || ""} onChange={(e) => setBank({ ...bank, branch: e.target.value })} /></div>
          <div className="sm:col-span-2">
            <label className={`${label} mb-2`}>Status Aktif</label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={bank.is_active !== false} onChange={(e) => setBank({ ...bank, is_active: e.target.checked })} data-testid="bf-active" className="h-4 w-4 accent-[#C5A028]" />
              <span className="text-sm text-[#4A4844]">Tampilkan sebagai pilihan rekening tujuan pembayaran</span>
            </label>
          </div>
        </Modal>)}
    </ErpLayout>
  );
}
